import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../auth/AuthProvider";
import Breadcrumb from "../../../../core/common/Breadcrumb/breadcrumb";
import Sidebar from "../../../../core/common/sidebar/sidebar";
import { all_routes } from "../../../router/all_routes";
import styles from "./ChatPage.module.css";

export type MensagemDTO = {
  id: number;
  remetenteId: number;
  remetenteNome: string;
  conteudo: string;
  dataHora: string;
  tipo: "USUARIO" | "MOTORISTA";
  visualizada: boolean;
};

const ChatPage: React.FC = () => {
  const { compraId } = useParams<{ compraId: string }>();
  const { user, isLoggedIn, isDriver, loading } = useAuth();

  const [mensagens, setMensagens] = useState<MensagemDTO[]>([]);
  const [conteudo, setConteudo] = useState("");

  const BASE_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const routes = all_routes;

  const breadcrumbs = isDriver
    ? [
        { label: "My Tours", link: routes.driverTours, active: false },
        { label: "Chat", active: true },
      ]
    : [
        { label: "My Bookings", link: routes.userTukTukBooking, active: false },
        { label: "Chat", active: true },
      ];

  // modal register se não estiver logado (aguarda loading terminar)
  useEffect(() => {
    if (!loading && (!isLoggedIn || !user)) {
      if (window.location.pathname.includes("/chat")) {
        const modal = document.getElementById("register-modal");
        if (modal) {
          const bootstrapModal = new (window as any).bootstrap.Modal(modal);
          bootstrapModal.show();
        }
      }
    }
  }, [loading, isLoggedIn, user]);

  // buscar mensagens
  const fetchMensagens = async () => {
    if (!compraId) return;
    try {
      const res = await axios.get<MensagemDTO[]>(
        `${BASE_URL}/pacotes/${compraId}/chat`,
        { headers }
      );
      setMensagens(res.data);

      await axios.post(
        `${BASE_URL}/pacotes/${compraId}/chat/visualizadas`,
        {},
        { headers }
      );
    } catch (err) {
      console.error("Erro ao buscar/marcar mensagens:", err);
    }
  };

  // enviar mensagem
  const enviarMensagem = async () => {
    if (!conteudo.trim() || !compraId) return;
    try {
      const res = await axios.post<MensagemDTO>(
        `${BASE_URL}/pacotes/${compraId}/chat`,
        { conteudo },
        { headers }
      );
      setMensagens((prev) => [...prev, res.data]);
      setConteudo("");
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
    }
  };

  // busca inicial quando user carregado / compraId muda
  useEffect(() => {
    if (!loading) fetchMensagens();
    // opcional: você pode adicionar polling aqui
  }, [compraId, loading]);

  // rolar para o final quando mensagens mudam
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mensagens]);

  return (
    <div>
      <Breadcrumb
        title={`Chat da Compra #${compraId}`}
        breadcrumbs={breadcrumbs}
        backgroundClass="breadcrumb-bg-01"
      />

      <div className="content">
        <div className="container">
          <div className="row">
            {/* Sidebar */}
            <div className="col-xl-3 col-lg-4">
              <Sidebar />
            </div>

            {/* Chat principal */}
            <div className="col-xl-9 col-lg-8">
              <div className="card shadow-none mb-0">
                <div className="card-header d-flex align-items-center">
                  <Link
                    to={isDriver ? routes.driverTours : routes.userTukTukBooking}
                    className={styles.backButton}
                  >
                    ← Back
                  </Link>
                  <h6 className="mb-0 ms-3">Chat da Compra #{compraId}</h6>
                </div>

                <div
                  className="card-body d-flex flex-column"
                  style={{ height: "70vh" }}
                >
                  {/* Lista de mensagens */}
                  <div className={`${styles.chatMessages} flex-grow-1 mb-3`}>
                    {mensagens.map((m) => {
                      // lógica: se o remetente for o usuário logado => mensagem "minha" (direita azul)
                      // caso contrário => mensagem do outro (esquerda cinza)
                      const isMy = user ? m.remetenteId === user.id : false;

                      return (
                        <div
                          key={m.id}
                          className={`${styles.message} ${
                            isMy ? styles.myMessage : styles.otherMessage
                          } ${!m.visualizada ? styles.unreadMessage : ""}`}
                        >
                          <div className={styles.messageHeader}>
                            <strong>{m.remetenteNome}</strong>
                            <span className={styles.messageTime}>
                              {new Date(m.dataHora).toLocaleTimeString()}
                            </span>
                          </div>
                          <div>{m.conteudo}</div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Campo de envio */}
                  <div className={styles.chatInput}>
                    <input
                      type="text"
                      className={styles.inputField}
                      value={conteudo}
                      onChange={(e) => setConteudo(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && enviarMensagem()}
                      placeholder="Digite sua mensagem..."
                    />
                    <button
                      className={styles.sendButton}
                      onClick={enviarMensagem}
                    >
                      Enviar
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {/* /col-xl-9 */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;